import { Asset, MetadataFieldType, MetadataValue } from '@/api/generatedApi';
import { ApiErrorAlert } from '@/components/Errors/ApiErrorAlert';
import { TextInput } from '@/components/Inputs/TextInput';
import { useUpdateAsset } from '@/hooks/api/assets/useUpdateAsset';
import { useCreateMetadataFields } from '@/hooks/api/metadata/useCreateMetadataFields';
import { useListAllMetadataFields } from '@/hooks/api/metadata/useListAllMetadataFields';
import { useGetCurrentUser } from '@/hooks/api/users/useGetCurrentUser';
import useSetState from '@/hooks/useSetState';
import { useToast } from '@/hooks/useToast';
import { AssetPageObjectModel } from '@/poms/assetPageObjectModel';
import { SIDEBAR_WIDTH } from '@/theme';
import { Autocomplete, Box, FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import { InputField } from '../../Inputs/InputField';
import { MetadataActions } from './MetadataActions';

interface MetadataAddProperties {
  asset: Asset;
  onSuccess?: (newMetadata: Record<string, MetadataValue>) => void;
  onCancel?: () => void;
}

export const MetadataForm = ({ asset, onSuccess, onCancel }: MetadataAddProperties) => {
  const { metadataFields, isLoading: isLoadingMetadataFields } = useListAllMetadataFields();
  const { createMetadataField, isCreating, createError } = useCreateMetadataFields();
  const { updateAsset, isUpdating } = useUpdateAsset();
  const { showToast } = useToast();
  const { permissions } = useGetCurrentUser();

  const [state, setState] = useSetState({
    fieldName: '',
    fieldValue: '',
    fieldType: MetadataFieldType.Text,
    isFieldNameNew: true,
  });

  const handleChangeState = (property: 'fieldName' | 'fieldValue' | 'fieldType', value: string | number | boolean) => {
    if (property === 'fieldName') {
      const existingField = metadataFields?.find(field => field.name === value);
      if (existingField) {
        setState({
          fieldName: value as string,
          fieldType: existingField.type,
          isFieldNameNew: false,
        });
      } else {
        setState({
          fieldName: value as string,
          fieldType: MetadataFieldType.Text,
          isFieldNameNew: true,
        });
      }
    } else {
      setState({ [property]: value });
    }
  };

  const handleSaveField = async () => {
    if (
      !asset?.id ||
      !state.fieldName ||
      (state.fieldValue === '' && state.fieldValue === undefined) ||
      !state.fieldType
    ) {
      return;
    }

    try {
      const fieldExists = metadataFields?.some(field => field.name === state.fieldName);

      if (!fieldExists) {
        await new Promise<void>((resolve, reject) => {
          createMetadataField(
            {
              name: state.fieldName,
              type: state.fieldType,
            },
            {
              onSuccess: () => resolve(),
              onError: (error: Error) => reject(error),
            }
          );
        });
      }

      updateAsset(
        {
          assetId: asset.id,
          data: {
            metadata: {
              [state.fieldName]: state.fieldValue,
            },
          },
        },
        {
          onSuccess: () => {
            showToast({
              message: `Successfully added "${state.fieldName}" metadata`,
              type: 'success',
            });
            onSuccess?.({
              [state.fieldName]: state.fieldValue,
            });
          },
          onError: () => {
            showToast({
              message: `Failed to add "${state.fieldName}" metadata`,
              type: 'error',
            });
          },
        }
      );
    } catch {
      console.error('Failed to save metadata');
    }
  };

  const isFreeSoloActive = permissions?.canAddMetadataFields ?? false;
  const isFreeSoloEntryValid = isFreeSoloActive ? true : metadataFields?.some(field => field.name === state.fieldName);
  const isValid = state.fieldName && state.fieldValue !== '' && state.fieldType && isFreeSoloEntryValid;

  return (
    <Box sx={{ p: 1, minWidth: SIDEBAR_WIDTH - 12 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, justifyContent: 'flex-start' }}>
        {createError && <ApiErrorAlert error={createError} />}
        <Autocomplete
          data-testid={AssetPageObjectModel.AddMetadataFieldName}
          options={metadataFields?.map(field => field.name) ?? []}
          value={state.fieldName}
          onChange={(_event, newValue) => handleChangeState('fieldName', newValue || '')}
          onInputChange={(_event, newInputValue) => handleChangeState('fieldName', newInputValue || '')}
          loading={isLoadingMetadataFields}
          freeSolo={isFreeSoloActive}
          disableClearable
          getOptionDisabled={option => Object.keys(asset.metadata || {}).includes(option)}
          getOptionLabel={option => {
            const existingField = Object.keys(asset.metadata || {}).includes(option);
            return existingField ? `${option} (exists)` : option;
          }}
          renderInput={parameters => <TextInput autoFocus {...parameters} label='Add metadata' />}
        />

        <FormControl fullWidth>
          <InputLabel id='type-select-label'>Type</InputLabel>
          <Select
            disabled={!state.isFieldNameNew}
            labelId='type-select-label'
            label='Type'
            size='small'
            value={state.fieldType}
            onChange={event => handleChangeState('fieldType', event.target.value)}
            sx={{ borderRadius: '8px' }}
            data-testid={AssetPageObjectModel.AddMetadataFieldType}
          >
            <MenuItem value={MetadataFieldType.Text}>Text</MenuItem>
            <MenuItem value={MetadataFieldType.Number}>Number</MenuItem>
            <MenuItem value={MetadataFieldType.Boolean}>Boolean</MenuItem>
            <MenuItem value={MetadataFieldType.Url}>Link</MenuItem>
          </Select>
        </FormControl>

        <InputField
          fieldType={state.fieldType}
          value={state.fieldValue}
          onChange={value => handleChangeState('fieldValue', value)}
          placeholder='Value'
          data-testid={AssetPageObjectModel.AddMetadataFieldValue}
        />

        <MetadataActions
          onSave={() => {
            handleSaveField();
          }}
          onCancel={onCancel}
          isSaveDisabled={!isValid}
          isLoading={isCreating || isUpdating}
        />
      </Box>
    </Box>
  );
};
